import { useEffect, useRef, useState } from "react";
import { Award, Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmt, today } from "@/lib/format";
import { Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function EosDialog({ employee, onClose }) {
  const { t } = useLang();
  const [asOf, setAsOf] = useState(today());
  const [d, setD] = useState(null);
  useEffect(() => { if (employee) api.get(`/payroll/employees/${employee.id}/eos`, { params: { as_of: asOf } }).then((r) => setD(r.data)); }, [employee, asOf]);
  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="eos-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("eosTitle")} — {employee?.name}</DialogTitle><DialogDescription>{t("eosText")}</DialogDescription></DialogHeader>
        <div className="flex items-center gap-3 text-sm"><span>{t("asOf")}</span><Input data-testid="eos-asof" type="date" className="w-44 font-mono" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
        {d && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["hireDate", employee.hire_date || "—", "eos-hire"], ["tenure", `${d.years} ${t("years")}`, "eos-years"], ["leaveAccrued", `${d.leave_accrued} j`, "eos-accrued"], ["leaveTaken", `${d.leave_taken} j`, "eos-taken"]].map(([k, v, tid]) => (
              <div key={k} className="rounded-lg border border-slate-200 p-3"><div className="text-xs text-slate-500">{t(k)}</div><div data-testid={tid} className="font-mono font-semibold">{v}</div></div>
            ))}
            <div className="rounded-lg border border-slate-200 p-3"><div className="text-xs text-slate-500">{t("leaveBalance")}</div><div data-testid="eos-leave-balance" className="font-mono font-semibold">{d.leave_balance} j · AED {fmt(d.leave_value)}</div></div>
            <div className="rounded-lg bg-emerald-50 p-3"><div className="text-xs text-emerald-800">{t("gratuity")}</div><Money testid="eos-gratuity" value={d.gratuity} className="text-lg font-bold text-[#007A3D]" /></div>
          </div>
        )}
        <p className="text-[11px] text-slate-400">{t("gratuityRule")}</p>
      </DialogContent>
    </Dialog>
  );
}

export function EosProvision() {
  const { t } = useLang();
  const [st, setSt] = useState(null);
  const load = () => api.get("/payroll/eos-provision").then((r) => setSt(r.data));
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const book = async () => { try { const r = await api.post("/payroll/eos-provision"); setSt(r.data); toast.success(r.data.booked ? t("eosBooked") : t("ctProvisionNone")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4 text-sm" data-testid="eos-provision-box">
      <div className="flex items-center gap-2"><Award className="h-4 w-4 text-[#D4AF37]" /><span className="font-semibold">{t("eosProvision")}</span><span className="text-slate-500">{t("eosTotal")}: <Money testid="eos-total" value={st.total_gratuity} className="font-semibold" /> · {t("ctProvisionBooked")}: <Money value={st.booked} /> · {t("ctProvisionGap")}: <Money testid="eos-gap" value={st.gap} /></span></div>
      <Button size="sm" variant="outline" data-testid="eos-provision-btn" disabled={Math.abs(st.gap) < 0.01} onClick={book}>{t("eosBook")}</Button>
    </div>
  );
}

export function ImportDialog({ open, onClose, onDone }) {
  const { t } = useLang();
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (dry) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/employees/import?dry_run=${dry}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (dry) setPreview(data); else { toast.success(`${t("imported")}: ${data.imported}`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const template = async () => { const r = await api.get("/payroll/employees/template.xlsx", { responseType: "blob" }); const u = URL.createObjectURL(r.data); Object.assign(document.createElement("a"), { href: u, download: "mohassib-employees-template.xlsx" }).click(); URL.revokeObjectURL(u); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPreview(null); setFile(null); onClose(); } }}>
      <DialogContent data-testid="import-dialog" className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">{t("importEmployees")}</DialogTitle><DialogDescription>{t("importText")}</DialogDescription></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" data-testid="import-template-btn" onClick={template}><Download className="me-1 h-4 w-4" />{t("downloadTemplate")}</Button>
          <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid="import-file-input" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          <Button variant="outline" size="sm" data-testid="import-pick-btn" onClick={() => ref.current?.click()}><Upload className="me-1 h-4 w-4" />{file ? file.name : t("chooseFile")}</Button>
          <Button size="sm" data-testid="import-preview-btn" disabled={!file || busy} onClick={() => send(true)} className="bg-[#0B1220] text-white hover:bg-slate-800">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />}{t("preview")}</Button>
        </div>
        {preview && (
          <>
            <div className="max-h-[40vh] overflow-auto">
              <table className="data-table compact">
                <thead><tr><th>#</th><th>{t("name")}</th><th>MOL</th><th>IBAN</th><th className="text-end">{t("basicSalary")}</th><th>{t("hireDate")}</th><th>{t("errors")}</th></tr></thead>
                <tbody>{preview.rows.map((r) => <tr key={r.row} data-testid={`import-row-${r.row}`} className={r.errors.length ? "bg-red-50/60" : ""}><td>{r.row}</td><td>{r.name}</td><td className="font-mono text-xs">{r.mol_id}</td><td className="font-mono text-xs">{r.iban}</td><td className="text-end font-mono">{r.basic ?? "—"}</td><td className="font-mono text-xs">{r.hire_date}</td><td className="text-xs text-[#CE1126]">{r.errors.join(", ")}</td></tr>)}</tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-sm"><span data-testid="import-summary">{preview.valid} {t("validRows")} · {preview.invalid} {t("invalidRows")}</span><Button data-testid="import-confirm-btn" disabled={!preview.valid || busy} onClick={() => send(false)} className="bg-[#007A3D] hover:bg-[#005E2E]">{t("importValid")}</Button></div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
